import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { RUN_ID_PARAM } from '@/constants/workflow-editor';

export interface DeepLinkParams {
  runId: string | null;
}

/**
 * The host's side of the deep link for an editor mounted inside a pane,
 * which cannot read or write the address bar itself.
 */
export interface HostRunBinding {
  /**
   * The workflow the pane shows. A write made while another workflow was on
   * screen -- a run dispatch that resolved after the pane moved on -- is
   * dropped rather than pinned on the wrong workflow.
   */
  workflowId?: string;
  /** The run the host names for the pane; read in place of `?runId=`. */
  runId?: string | null;
  /**
   * Told each run the editor puts on screen, or null when it clears one --
   * the writes `setUrlState` and `clearUrlState` would otherwise make to
   * `?runId=` -- so the host can keep its own URL naming what is shown.
   */
  onRunChange?: (runId: string | null) => void;
}

function buildUrl(
  pathname: string,
  params: Partial<DeepLinkParams>,
  base?: URLSearchParams,
) {
  const searchParams = base ?? new URLSearchParams(window.location.search);

  if (params.runId !== undefined) {
    if (params.runId) {
      searchParams.set(RUN_ID_PARAM, params.runId);
    } else {
      searchParams.delete(RUN_ID_PARAM);
    }
  }

  const search = searchParams.toString();
  // The hash is carried through: a session resumed after an idle sign-out
  // replays the whole address bar, and applying the run would strip it.
  return `${pathname}${search ? `?${search}` : ''}${window.location.hash}`;
}

/**
 * @param enabled When false the hook stops touching the URL: an editor mounted
 *   inside a host pane shares the host's address bar, so syncing `?runId=`
 *   there would corrupt the host's own query state. The run is read from
 *   `host.runId` instead and every write goes to `host.onRunChange`.
 */
export function useWorkflowDeepLink(
  enabled: boolean = true,
  host: HostRunBinding = {},
) {
  const navigate = useNavigate();
  const location = useLocation();
  // Read at call time so a host passing a fresh callback each render does
  // not churn setUrlState, which sits in dependency arrays all over the editor.
  const onRunChangeRef = useRef(host.onRunChange);
  onRunChangeRef.current = host.onRunChange;
  const hostWorkflowId = host.workflowId;
  const hostWorkflowIdRef = useRef(hostWorkflowId);
  hostWorkflowIdRef.current = hostWorkflowId;
  // A dispatch or run load still in flight when the pane closed must not
  // reopen it through a late write, and one that resolved after the pane
  // moved to another workflow must not pin its run on that one. The writers
  // close over the workflow they were created for and compare it to the
  // current one at call time. Standalone, a late write would likewise
  // rewrite whatever page the user has moved on to.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const initialParamsRef = useRef<DeepLinkParams | null>(null);
  const capturedPathnameRef = useRef<string | null>(null);

  // `setUrlState` syncs the applied run back to the URL via
  // `window.history.replaceState`, which does not update react-router's
  // `location`, so re-capturing on a changed `runId` here only fires for real
  // navigations (e.g. clicking a notification for another run while already on
  // this workflow) — not for our own URL bookkeeping.
  const searchRunId = new URLSearchParams(location.search).get(RUN_ID_PARAM);
  if (
    initialParamsRef.current === null ||
    capturedPathnameRef.current !== location.pathname ||
    (searchRunId !== null && searchRunId !== initialParamsRef.current.runId)
  ) {
    initialParamsRef.current = {
      runId: searchRunId,
    };
    capturedPathnameRef.current = location.pathname;
  }

  const initialParams = initialParamsRef.current;

  const setUrlState = useCallback(
    (params: Partial<DeepLinkParams>) => {
      if (!mountedRef.current) return;
      if (!enabled) {
        if (params.runId === undefined) return;
        if (hostWorkflowId !== hostWorkflowIdRef.current) return;
        onRunChangeRef.current?.(params.runId);
        return;
      }
      const url = buildUrl(window.location.pathname, params);
      window.history.replaceState(null, '', url);
    },
    [enabled, hostWorkflowId],
  );

  const clearUrlState = useCallback(() => {
    if (!mountedRef.current) return;
    if (!enabled) {
      if (hostWorkflowId !== hostWorkflowIdRef.current) return;
      onRunChangeRef.current?.(null);
      return;
    }
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete(RUN_ID_PARAM);
    const search = searchParams.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ''}`, {
      replace: true,
    });
  }, [enabled, hostWorkflowId, location.pathname, navigate]);

  return {
    initialParams: enabled ? initialParams : { runId: host.runId ?? null },
    setUrlState,
    clearUrlState,
  };
}
